import React from 'react';
import { X, Check, Trash2, ShieldAlert, Sparkles } from 'lucide-react';
import { LoadingSpinner, InlineLoadingState } from './LoadingSpinner';
import { AdminSuppliersTab } from '../features/admin/components/AdminSuppliersTab';
import { AdminDatanormTab } from '../features/admin/components/AdminDatanormTab';
import { AdminKiReviewTab } from '../features/admin/components/AdminKiReviewTab';

interface PendingUser {
  user_id: string;
  email: string;
  display_name: string | null;
  registered_at: string;
}

function getInitials(name: string | null, email: string): string {
  const src = name?.trim() || email;
  const parts = src.split(/[\s@]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return src.slice(0, 2).toUpperCase();
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  onClose: () => void;
}

export function AdminPanel({ onClose }: Props) {
  const [activeTab, setActiveTab] = React.useState<'pending' | 'suppliers' | 'datanorm' | 'ki_review'>('pending');
  const [pending, setPending] = React.useState<PendingUser[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [actionLoading, setActionLoading] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/admin/pending', { credentials: 'include' });
      if (!res.ok) throw new Error('Fehler beim Laden.');
      const data = await res.json() as { pending: PendingUser[] };
      setPending(data.pending ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { void load(); }, [load]);

  const handleApprove = async (userId: string) => {
    setActionLoading(userId + ':approve');
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/approve/${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(payload?.error ?? 'Fehler beim Freigeben.');
      }
      setPending((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Fehler.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId: string) => {
    setActionLoading(userId + ':reject');
    setActionError(null);
    try {
      const res = await fetch(`/api/admin/reject/${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(payload?.error ?? 'Fehler beim Ablehnen.');
      }
      setPending((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Fehler.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10001] flex items-center justify-center p-4 bg-black/40"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-4xl rounded-xl border border-app-border bg-app-surface shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Super Admin"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-app-border bg-violet-50">
          <ShieldAlert className="w-5 h-5 text-violet-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold text-app-text">Super Admin</h2>
            <p className="text-xs text-app-text-subtle">Registrierungen, Lieferanten und KI-Review</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:bg-app-hover hover:text-app-text-muted transition-colors"
            aria-label="Schließen"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 pt-3 border-b border-app-border bg-app-surface">
          <div className="inline-flex rounded-lg border border-app-border overflow-hidden">
            <button
              type="button"
              onClick={() => setActiveTab('pending')}
              className={`px-3 py-1.5 text-xs font-medium ${activeTab === 'pending' ? 'bg-violet-100 text-violet-700' : 'bg-app-surface text-app-text-muted hover:bg-app-hover'}`}
            >
              Registrierungen
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('suppliers')}
              className={`px-3 py-1.5 text-xs font-medium border-l border-app-border ${activeTab === 'suppliers' ? 'bg-violet-100 text-violet-700' : 'bg-app-surface text-app-text-muted hover:bg-app-hover'}`}
            >
              Lieferanten
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('datanorm')}
              className={`px-3 py-1.5 text-xs font-medium border-l border-app-border ${activeTab === 'datanorm' ? 'bg-violet-100 text-violet-700' : 'bg-app-surface text-app-text-muted hover:bg-app-hover'}`}
            >
              Datanorm Verwaltung
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('ki_review')}
              className={`px-3 py-1.5 text-xs font-medium border-l border-app-border inline-flex items-center gap-1 ${activeTab === 'ki_review' ? 'bg-violet-100 text-violet-700' : 'bg-app-surface text-app-text-muted hover:bg-app-hover'}`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              KI-Review
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {actionError && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {actionError}
            </div>
          )}

          {activeTab === 'suppliers' ? (
            <AdminSuppliersTab />
          ) : activeTab === 'datanorm' ? (
            <AdminDatanormTab />
          ) : activeTab === 'ki_review' ? (
            <AdminKiReviewTab />
          ) : loading ? (
            <InlineLoadingState label="Laden…" className="py-10" />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-sm text-red-600">{error}</p>
              <button
                type="button"
                onClick={() => void load()}
                className="mt-3 text-xs text-violet-600 hover:underline"
              >
                Erneut versuchen
              </button>
            </div>
          ) : pending.length === 0 ? (
            <div className="text-center py-10">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-sm font-medium text-app-text-muted">Keine ausstehenden Registrierungen</p>
              <p className="text-xs text-slate-400 mt-1">Alles erledigt.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {pending.map((user) => {
                const approving = actionLoading === user.user_id + ':approve';
                const rejecting = actionLoading === user.user_id + ':reject';
                const busy = approving || rejecting;
                return (
                  <li
                    key={user.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-app-border bg-app-surface-muted"
                  >
                    <div className="w-9 h-9 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-bold shrink-0">
                      {getInitials(user.display_name, user.email)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-app-text truncate">
                        {user.display_name || user.email.split('@')[0]}
                      </div>
                      <div className="text-xs text-app-text-subtle truncate">{user.email}</div>
                      <div className="text-xs text-slate-400 mt-0.5">{formatDate(user.registered_at)}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleApprove(user.user_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 transition-colors"
                        title="Freigeben"
                      >
                        {approving ? (
                          <LoadingSpinner size="xs" />
                        ) : (
                          <Check className="w-3.5 h-3.5" />
                        )}
                        Freigeben
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleReject(user.user_id)}
                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
                        title="Ablehnen"
                      >
                        {rejecting ? (
                          <LoadingSpinner size="xs" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                        Ablehnen
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {activeTab === 'pending' && !loading && pending.length > 0 && (
          <div className="px-5 py-3 border-t border-app-border bg-app-surface-muted text-xs text-slate-400 text-right">
            {pending.length} ausstehend
          </div>
        )}
      </div>
    </div>
  );
}
